/**
 * GlobalAnalysisProgress - Persistent Progress Bar (UX v2.0)
 *
 * OPARA v15.1: Progress Bar Fix - queued/pending status support
 * OPARA v14.2: Real-time Progress Sync - Backend'den canlı veri
 *
 * coklu-kutu C2b-1: TEK kutu -> COKLU (yan yana, max 2). activeAnalyses.map ile her task
 * KENDI kutusunu render eder (a.* degerleri; shim DEGIL). Dis wrapper konumlandirir
 * (fixed bottom-[72px] right-4 + flex-row-reverse: eski/ilk task sag-altta sabit, yeni sola acilir).
 * Ic kutudan fixed/bottom/right KALDIRILDI (w-80 KALIR). dismiss/retry per-task (a.task_id).
 * Guard/buton/sonuc-tuketimi C2b-2/3.
 *
 * Features:
 * - Fixed position bottom-right (wrapper), yan yana stack (dar ekranda flex-wrap alt alta)
 * - Shows REAL progress from backend (per-task)
 * - i18n support for all status messages (per-task a.locale)
 * - 7-minute timeout maintenance message
 * - "View Report" button on completion
 * - v15.1: Proper handling of queued/pending statuses
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAnalysisTracker } from '../contexts/AnalysisTrackerContext';
import { X, Loader2, CheckCircle2, XCircle, ExternalLink } from 'lucide-react';

// Import translations
import trTranslations from '../locales/tr.json';
import enTranslations from '../locales/en.json';
import deTranslations from '../locales/de.json';
import frTranslations from '../locales/fr.json';
import esTranslations from '../locales/es.json';
import arTranslations from '../locales/ar.json';

const translations = {
  tr: trTranslations,
  en: enTranslations,
  de: deTranslations,
  fr: frTranslations,
  es: esTranslations,
  ar: arTranslations
};

// --- Pure helpers (hook YOK -> .map icinde guvenli cagrilir) ---

// Status-specific styles (per-task a.status)
const getStatusStyles = (status) => {
  switch (status) {
    case 'completed':
      return {
        bg: 'bg-green-900/95',
        border: 'border-green-500',
        progressBg: 'bg-green-500',
        icon: <CheckCircle2 className="w-5 h-5 text-green-400" />
      };
    case 'failed':
      return {
        bg: 'bg-red-900/95',
        border: 'border-red-500',
        progressBg: 'bg-red-500',
        icon: <XCircle className="w-5 h-5 text-red-400" />
      };
    default:
      return {
        bg: 'bg-gray-900/95',
        border: 'border-[#FFDD59]',
        progressBg: 'bg-[#FFDD59]',
        icon: <Loader2 className="w-5 h-5 text-[#FFDD59] animate-spin" />
      };
  }
};

// Status header text (per-task)
const getStatusHeader = (status, t) => {
  switch (status) {
    case 'completed':
      return t.analysis_complete || 'Analiz Tamamlandı';
    case 'failed':
      return t.analysis_failed || 'Analiz Başarısız';
    default:
      return t.analysis_in_progress || 'Analiz Devam Ediyor';
  }
};

// v14.2: Display message - backend message or i18n fallback (per-task)
const getDisplayMessage = (a, t) => {
  const message = a.message;
  const status = a.status;
  // If we have a message from backend, try to translate it or use as-is
  if (message) {
    // Check if message is a progress key from backend (i18n key)
    if (t[message]) {
      return t[message];
    }
    return message;
  }
  // No message - show appropriate default based on status
  if (status === 'queued' || status === 'pending') {
    return t.queued || 'Sırada bekleniyor...';
  }
  // Default for processing or any other status
  return t.processing || 'İşleniyor...';
};

const GlobalAnalysisProgress = () => {
  const navigate = useNavigate();
  const { lang } = useParams();
  const { activeAnalyses, dismissAnalysis, retryAnalysis } = useAnalysisTracker();

  // Don't render if no active analysis
  if (activeAnalyses.length === 0) {
    return null;
  }

  return (
    // Dis wrapper: konumlandirici. flex-row-reverse -> ilk/eski task sag-altta sabit, yeni sola acilir.
    // flex-wrap + max-w -> dar ekranda alt alta kayar.
    <div className="fixed bottom-[72px] right-4 z-[9999] flex flex-row-reverse gap-2 max-w-[calc(100vw-2rem)] flex-wrap">
      {activeAnalyses.map((a) => {
        // Per-task deriv (shim DEGIL - her kutu KENDI a.* degerini kullanir)
        const status = a.status;
        const progress = a.progress;
        const username = a.username;
        const task_id = a.task_id;
        const locale = a.locale;

        const currentLocale = locale || lang || 'tr';
        const t = translations[currentLocale]?.progress || translations.tr.progress;

        const styles = getStatusStyles(status);
        const displayMessage = getDisplayMessage(a, t);

        return (
          <div
            key={task_id}
            className={`w-80 ${styles.bg} backdrop-blur-md rounded-xl border ${styles.border} shadow-2xl overflow-hidden`}
            data-testid="global-analysis-progress"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
              <div className="flex items-center gap-2">
                {styles.icon}
                <span className="text-sm font-medium text-white">
                  {getStatusHeader(status, t)}
                </span>
              </div>
              <button
                onClick={() => dismissAnalysis(task_id)}
                className="p-1 hover:bg-gray-700 rounded-lg transition-colors"
                data-testid="dismiss-analysis-btn"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="px-4 py-3">
              {/* Username */}
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">
                    {username?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
                <span className="text-white font-medium">@{username}</span>
              </div>

              {/* Progress Bar (for processing and any non-terminal state) */}
              {/* v15.3 FIX: Always show progress bar when not completed/failed */}
              {/* Added min-width to ensure visibility even at 0% */}
              {status !== 'completed' && status !== 'failed' && (
                <>
                  {/* Progress Bar with % aligned to right */}
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${styles.progressBg} transition-all duration-500 ease-out`}
                        style={{ width: `${Math.max(Math.min(progress || 0, 100), 5)}%` }}
                      />
                    </div>
                    <span className="text-[#FFDD59] font-bold text-xs whitespace-nowrap">
                      %{Math.round(progress || 0)}
                    </span>
                  </div>
                  {/* Status message - full width, wrapping allowed */}
                  <p className="text-xs text-gray-400 leading-relaxed">
                    {displayMessage}
                  </p>
                </>
              )}

              {/* Completed Actions */}
              {status === 'completed' && (
                <button
                  onClick={() => navigate(`/${locale || 'tr'}/rapor/${task_id}`)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg transition-colors"
                  data-testid="view-report-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t.view_report || 'Raporu Görüntüle'}
                </button>
              )}

              {/* v30.67: FAILED aciklama satiri kaldirildi — kutu sadece durum gosterir
                  (header "Analiz Basarisiz" + avatar/@username + dismiss X). Aciklama ekran
                  ortasindaki ana mesajda (useAnalyzeReel error_message, kategori mesaji). */}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default GlobalAnalysisProgress;
